/**
 * AutoInjectDialog — Multi-Cashier Shifts smart auto-inject confirmation.
 *
 * Surfaced when a money-out action (expense, PO cash payment, customer
 * cashout, employee advance, return refund) detects that the active
 * cashier's sub-drawer is short. Manager PIN authorizes a full-bill-amount
 * transfer from the branch Safe into the cashier's sub-drawer, after which
 * the original action proceeds.
 *
 * Key UX rules:
 *  • Inject amount = FULL bill (NOT just the shortfall) so operating float
 *    is preserved after payment.
 *  • Reason is auto-filled from the originating action description but
 *    remains editable.
 *  • Cancel returns the user to the original modal with their inputs intact.
 */
import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, ArrowDown, Banknote, Lock } from 'lucide-react';
import { formatPHP } from '../../lib/utils';

export default function AutoInjectDialog({
  open,
  onClose,
  onConfirm,         // (pin, reason) => Promise<void>
  subDrawerBalance,  // current sub-drawer balance
  needed,            // amount needed
  injectAmount,      // FULL bill amount (server-suggested)
  suggestedReason,
  actionLabel = 'this expense', // e.g. "this PO payment"
}) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPin('');
      setReason(suggestedReason || '');
      setSubmitting(false);
    }
  }, [open, suggestedReason]);

  if (!open) return null;

  const shortfall = Math.max(0, needed - subDrawerBalance);
  const newBalance = subDrawerBalance + injectAmount;
  const afterPayment = newBalance - needed;

  const handleConfirm = async () => {
    if (!pin || pin.length < 4) return;
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(pin.trim(), reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // pointerEvents:'auto' — layer audit 2026-09-01: when a Radix modal
    // dialog is open underneath, it sets body pointer-events:none; without
    // this the reason/PIN inputs paint on top but eat no clicks (the
    // OS-MB-001545 trap). Callers ALSO demote their dialog to non-modal.
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, pointerEvents: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      data-testid="auto-inject-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full p-5 animate-in fade-in zoom-in-95 duration-200"
        style={{ maxWidth: '460px' }}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="font-bold text-slate-800">Insufficient Sub-Drawer Balance</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Auto-inject from Safe to cover {actionLabel}?
            </p>
          </div>
        </div>

        {/* Balance breakdown */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 mb-4">
          <Row label="Your sub-drawer" value={subDrawerBalance} mono />
          <Row label="Required" value={needed} mono negative />
          {shortfall > 0 && (
            <Row label="Shortfall" value={shortfall} mono negative bold />
          )}
          <div className="border-t border-slate-200 my-1.5" />
          <Row
            label="Auto-transfer from Safe"
            value={injectAmount}
            mono
            highlight
            icon={<ArrowDown size={11} className="text-emerald-600" />}
          />
          <p className="text-[10px] text-slate-500 italic leading-snug pl-1">
            Full bill amount — keeps your operating float intact after payment.
          </p>
          <div className="border-t border-slate-200 my-1.5" />
          <Row label="Sub-drawer after inject" value={newBalance} mono muted />
          <Row label="Sub-drawer after payment" value={afterPayment} mono bold />
        </div>

        {/* Reason */}
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-600 font-medium">
              Transfer Reason <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Pay BPI electric bill"
              className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
              data-testid="auto-inject-reason" />
            <p className="text-[10px] text-slate-400 mt-0.5">
              Permanently logged on both the Safe and your sub-drawer ledger.
            </p>
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium flex items-center gap-1">
              <Shield size={11} /> Manager PIN <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={pin}
              autoComplete="one-time-code" data-lpignore="true"
              onChange={e => setPin(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleConfirm()}
              placeholder="Enter PIN"
              className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 font-mono text-center text-xl tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
              data-testid="auto-inject-pin" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            data-testid="auto-inject-cancel">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !pin || pin.length < 4 || !reason.trim()}
            className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="auto-inject-confirm">
            {submitting ? (
              <>Processing...</>
            ) : (
              <><Lock size={13} /> Confirm Transfer + Pay</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, negative, bold, highlight, muted, icon }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className={`flex items-center gap-1.5 ${muted ? 'text-slate-400' : 'text-slate-600'}`}>
        {icon}{label}
      </span>
      <span className={[
        mono ? '' : '',
        bold ? 'font-bold' : 'font-semibold',
        negative ? 'text-red-600' : '',
        highlight ? 'text-emerald-700' : '',
        muted ? 'text-slate-400' : '',
      ].join(' ').trim()}>
        {negative ? '−' : ''}{formatPHP(Math.abs(value || 0))}
      </span>
    </div>
  );
}

// Helper to detect the structured error from backend
export function isInsufficientSubDrawerError(err) {
  const detail = err?.response?.data?.detail;
  return detail && typeof detail === 'object' && detail.code === 'insufficient_sub_drawer_needs_inject';
}

// Helper to detect the "no shift open" structured error
export function isShiftRequiredError(err) {
  const detail = err?.response?.data?.detail;
  return detail && typeof detail === 'object' && detail.code === 'shift_required';
}
